import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, X } from "lucide-react";
import { ondiApi } from "../lib/ondi";

/**
 * Smart response suggestions — 3 chips over the composer.
 *
 * Fetches suggestions from /api/ondi/suggest when the conversation shows an
 * incoming message the user hasn't responded to yet. On tap, calls `onPick`
 * which is expected to send that text as a message.
 *
 * Never blocks the UI: on any failure the chips simply don't appear.
 */
export function SmartSuggestions({ conversationId, lastIncomingId, currentUserId, lastMessage, onPick }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastFetchedRef = useRef(null);

  const shouldOffer =
    !!conversationId &&
    !!lastMessage &&
    lastMessage.sender_id !== currentUserId &&
    !lastMessage.is_auto;

  const fetchSuggestions = async (force = false) => {
    if (!conversationId) return;
    if (!force && lastFetchedRef.current === lastIncomingId) return;
    lastFetchedRef.current = lastIncomingId;
    setLoading(true);
    try {
      const data = await ondiApi.suggest(conversationId);
      const arr = Array.isArray(data.suggestions) ? data.suggestions : [];
      setSuggestions(arr);
    } catch (_e) {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDismissed(false);
    setSuggestions([]);
    if (!shouldOffer) return;
    fetchSuggestions();
  }, [conversationId, lastIncomingId, shouldOffer]);

  if (!shouldOffer || dismissed) return null;
  if (!loading && suggestions.length === 0) return null;

  return (
    <div
      className="mx-0 sm:mx-4 px-3 pt-2 pb-1 flex items-center gap-2 overflow-x-auto no-scrollbar"
      data-testid="smart-suggestions"
    >
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 shrink-0">
        <Sparkles size={11} className="q-accent-text" />
        <span>ONDI sugiere</span>
      </div>
      {loading && suggestions.length === 0 && (
        <div
          className="text-xs text-slate-400 italic"
          data-testid="smart-suggestions-loading"
        >
          pensando…
        </div>
      )}
      {suggestions.map((s, i) => (
        <button
          key={`${i}-${s.slice(0, 20)}`}
          type="button"
          onClick={() => {
            setDismissed(true);
            onPick?.(s);
          }}
          className="q-btn-ghost !py-1.5 !px-3 rounded-full text-xs whitespace-nowrap border border-white/10 hover:border-[color:var(--accent)] transition-colors"
          style={{ color: "var(--accent)" }}
          data-testid={`smart-suggestion-${i}`}
        >
          {s}
        </button>
      ))}
      {suggestions.length > 0 && (
        <button
          type="button"
          onClick={() => fetchSuggestions(true)}
          className="text-slate-500 hover:text-slate-200 p-1"
          aria-label="Regenerar sugerencias"
          data-testid="smart-suggestions-refresh"
        >
          <RefreshCw size={13} />
        </button>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-slate-500 hover:text-slate-200 p-1"
        aria-label="Descartar sugerencias"
        data-testid="smart-suggestions-dismiss"
      >
        <X size={13} />
      </button>
    </div>
  );
}
